<template>
  <v-card>
    <v-form @submit.prevent="submitEditAllocation()" ref="editAllocationForm">
      <v-container>
        <v-layout wrap>
          <dialog-header
            :title="'Edit'"
            :details="`Allocation (${allocation.id})`"
            :time="editedAllocation.updatedAt"
          ></dialog-header>
          <flex-sub-header>Details</flex-sub-header>
          <v-flex xs12 sm6>
            <v-text-field
              label="Details"
              :color="primaryNeutral"
              v-model="editedAllocation.details"
              :rules="detailsRules"
              prepend-icon="note"
              :disabled="allocation.void || closed"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Site"
              :color="primaryNeutral"
              :items="sites"
              v-model="editedAllocation.siteId"
              item-text="name"
              item-value="id"
              :loading="loadingSites"
              :rules="siteRules"
              :prepend-icon="$store.state.global.pages.site.icon"
              :disabled="allocation.void || closed"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <date-picker-wrapper
              label="* Date"
              v-model="editedAllocation.date"
              :color="primaryNeutral"
              :disabled="allocation.void || closed"
            ></date-picker-wrapper>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Sub-Contractor"
              color="primary"
              :items="contractors"
              v-model="contractorId"
              item-text="name"
              item-value="id"
              :loading="loadingContractors"
              :prepend-icon="$store.state.global.pages.contractor.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Driver"
              :color="primaryNeutral"
              :items="filteredDrivers"
              v-model="editedAllocation.driverId"
              item-text="name"
              item-value="id"
              :loading="loadingDrivers"
              :rules="driverRules"
              :prepend-icon="$store.state.global.pages.driver.icon"
              :disabled="allocation.void || closed"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Vehicle"
              :color="primaryNeutral"
              :items="filteredVehicles"
              v-model="editedAllocation.vehicleId"
              item-text="vehicleName_vehicleType"
              item-value="id"
              :loading="loadingVehicles"
              :rules="vehicleRules"
              :prepend-icon="$store.state.global.pages.vehicle.icon"
              :disabled="allocation.void || closed"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Tip"
              :color="primaryNeutral"
              :items="filteredTips"
              @change="editedAllocation.spoilId = null"
              v-model="editedAllocation.tipId"
              item-text="name"
              item-value="id"
              :loading="loadingTips"
              :rules="tipRules"
              :prepend-icon="$store.state.global.pages.tip.icon"
              clearable
              :disabled="allocation.void || closed"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Spoil"
              :color="primaryNeutral"
              :items="filteredSpoils"
              v-model="editedAllocation.spoilId"
              item-text="name"
              item-value="id"
              :loading="loadingSpoils"
              :rules="spoilRules"
              :prepend-icon="$store.state.global.pages.spoil.icon"
              clearable
              :disabled="allocation.void || closed"
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <date-picker-wrapper
              label="Docket Date"
              :rules="[]"
              v-model="editedAllocation.prelimDocketDate"
              :color="primaryNeutral"
              :disabled="allocation.void || closed"
            ></date-picker-wrapper>
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              label="External Docket #"
              :color="primaryNeutral"
              v-model="editedAllocation.prelimExternalRef"
              prepend-icon="note"
              :disabled="allocation.void || closed"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6>
            <v-switch
              :disabled="allocation.void || closed"
              v-model="editedAllocation.day"
              label="Day"
              color="amber darken-1"
              style="margin-top: 0px"
            ></v-switch>
          </v-flex>
          <v-flex xs12 v-if="allocation.void">
            <v-textarea
              auto-grow
              label="VOID Notes"
              rows="2"
              v-model="editedAllocation.voidNotes"
              disabled
            ></v-textarea>
          </v-flex>
        </v-layout>
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>

        <div v-if="different">
          <v-btn :color="primaryGrey" flat @click="discard()">Discard?</v-btn>
          <v-btn :color="primaryGreen" flat type="submit" :loading="loading">Save</v-btn>
        </div>
        <div v-else>
          <v-btn :color="primaryGrey" flat @click="close()">Close</v-btn>
        </div>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import Store from '@/store/computed/storeHelpers'
import AllocationService from '@/services/requests/allocationService'

// MIXINS
import AllocationValidation from '@/services/validation/allocationValidation'
import FilterDriverVehicles from '@/services/mixins/filterDriverVehicles'
import getDrivers from '@/services/mixins/getMasterFiles/getDrivers'
import getVehicles from '@/services/mixins/getMasterFiles/getVehicles'
import getVehicleTypes from '@/services/mixins/getMasterFiles/getVehicleTypes'
import getTips from '@/services/mixins/getMasterFiles/getTips'
import getSpoils from '@/services/mixins/getMasterFiles/getSpoils'
import getContractors from '@/services/mixins/getMasterFiles/getContractors'
import getSites from '@/services/mixins/getMasterFiles/getSites'
import FilterTipSpoils from '@/services/mixins/filterTipSpoils'
import FormatDates from '@/services/mixins/formatDates'
import BillingPeriods from '@/services/mixins/billingPeriods'

// COMPONENTS
import DialogHeader from '@/components/ui/dialogHeader'
import DatePickerWrapper from '@/components/ui/datePickerWrapper'

export default {
  mixins: [
    AllocationValidation,
    FilterDriverVehicles,
    getDrivers,
    getVehicles,
    getVehicleTypes,
    getTips,
    getSpoils,
    getContractors,
    getSites,
    FilterTipSpoils,
    FormatDates,
    BillingPeriods
  ],
  components: {
    DialogHeader,
    DatePickerWrapper
  },
  data() {
    return {
      editedAllocation: this.cloneAllocation(this.allocation),
      loading: false,
      prefix: 'editedAllocation',
      contractorId: null
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    discard() {
      this.editedAllocation = this.cloneAllocation(this.allocation)
      this.close()
    },
    submitEditAllocation() {
      if (!this.$refs.editAllocationForm.validate()) {
        this.snack({ text: 'Please correct form errors', color: 'red' })
        return
      }
      this.save()
    },
    save() {
      this.loading = true
      AllocationService.updateAllocation(this.editedAllocation)
        .then(result => {
          this.loading = false
          this.$store.commit('allocations/updateArrayStateByValue', {
            prop: 'allocations',
            value: this.allocation,
            editedValue: result.data.result.allocation
          })
          this.editedAllocation = this.cloneAllocation(
            result.data.result.allocation
          )
          this.snack({ text: 'Allocation was edited!', color: 'green' })
          this.close()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          this.snackError()
        })
    },
    cloneAllocation(allocation) {
      var editedAllocation = Object.assign({}, allocation)
      editedAllocation.date = this.makeComputerDate(editedAllocation.date)
      return editedAllocation
    }
  },
  computed: {
    different() {
      if (!this._.isEqual(this.allocation, this.editedAllocation)) {
        return true
      } else {
        return false
      }
    },
    closed() {
      return !!this.allocation.prelimDocketId
    },
    driverId() {
      return this.editedAllocation.driverId
    },
    vehicleId() {
      return this.editedAllocation.vehicleId
    },
    projectId() {
      return this.editedAllocation.site.projectId
    },
    tipId() {
      return this.editedAllocation.tipId
    },
    spoilId() {
      return this.editedAllocation.spoilId
    }
  },
  watch: {
    allocation(val) {
      if (val.void) {
        this.editedAllocation = this.cloneAllocation(this.allocation)
      }
    }
  },
  mounted() {
    this.contractorId = this.editedAllocation.driver.contractorId
  },
  props: {
    allocation: Object
  }
}
</script>

<style>
</style>
